import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Box,
  Select,
  Loader,
  Center,
  UnstyledButton,
} from '@mantine/core';
import { ArrowRight } from 'react-feather';
import { notifications } from '@mantine/notifications';
import { usersApi, type InternalUser } from '../../../services/api/users';
import { jobTitlesApi } from '../../../services/api/jobTitles';
import type { CohortWithDetails } from '../../../types';
import { useJobTitles } from '../../../hooks/useJobTitles';
import classes from './EditModals.module.css';

interface EditTeamAssignmentsModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

export function EditTeamAssignmentsModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditTeamAssignmentsModalProps) {
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string | null>>({});
  const { jobTitleGroups } = useJobTitles();

  // Fetch internal users when modal opens
  useEffect(() => {
    if (opened) {
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const data = await usersApi.listInternal();
          setUsers(data);
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to load users',
            color: 'red',
          });
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();

      // Initialize assignments from cohort job_title_assignments
      const initial: Record<string, string | null> = {};
      if (cohort.job_title_assignments && cohort.job_title_assignments.length > 0) {
        for (const assignment of cohort.job_title_assignments) {
          initial[assignment.job_title_id] = assignment.user_id || null;
        }
      }
      setAssignments(initial);
    }
  }, [opened, cohort]);

  const handleAssignmentChange = (jobTitleId: string, userId: string | null) => {
    setAssignments(prev => ({ ...prev, [jobTitleId]: userId }));
  };

  const handleAutoPopulate = () => {
    // Build lookup: job title name → user IDs with that role
    const roleToUsers = new Map<string, string[]>();
    for (const user of users) {
      if (user.job_title) {
        const existing = roleToUsers.get(user.job_title) || [];
        existing.push(user.id);
        roleToUsers.set(user.job_title, existing);
      }
    }

    // Fill only unassigned roles
    setAssignments(prev => {
      const updated = { ...prev };
      for (const group of jobTitleGroups) {
        for (const title of group.titles) {
          if (updated[title.id]) continue;
          const matchingUsers = roleToUsers.get(title.name);
          if (matchingUsers && matchingUsers.length === 1) {
            updated[title.id] = matchingUsers[0];
          }
        }
      }
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = Object.entries(assignments).map(([job_title_id, user_id]) => ({
        job_title_id,
        user_id,
      }));
      await jobTitlesApi.updateCohortAssignments(cohort.id, data);

      // Re-fetch cohort to get updated data including job_title_assignments
      const { cohortsApi } = await import('../../../services/api/cohorts');
      const updated = await cohortsApi.getById(cohort.id);
      onUpdate(updated);
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Team assignments updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update team assignments',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Format users for Select component
  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Team Assignments"
      size="lg"
    >
      {loadingUsers ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Stack gap="lg">
          <Group justify="flex-end">
            <UnstyledButton
              onClick={handleAutoPopulate}
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <Text size="sm" c="var(--medley-green-primary)" style={{ cursor: 'pointer' }}>
                Auto-populate
              </Text>
              <ArrowRight size={14} color="var(--medley-green-primary)" />
            </UnstyledButton>
          </Group>
          {jobTitleGroups.map(group => (
            <Box key={group.category}>
              <Text
                className={classes.categoryHeader}
                mb="sm"
                c={group.disabled ? 'dimmed' : undefined}
              >
                {group.category}
              </Text>
              {group.disabled ? (
                <Text size="xs" c="dimmed" fs="italic">
                  No roles assigned to this function.
                </Text>
              ) : (
                <Stack gap="sm">
                  {group.titles.map(title => (
                    <Group key={title.id} grow>
                      <Text size="sm" c="dimmed" style={{ flex: '0 0 180px' }}>
                        {title.name}
                      </Text>
                      <Select
                        placeholder="Select team member"
                        data={userOptions}
                        value={assignments[title.id]}
                        onChange={(value) => handleAssignmentChange(title.id, value)}
                        clearable
                        searchable
                        className={classes.roleSelect}
                      />
                    </Group>
                  ))}
                </Stack>
              )}
            </Box>
          ))}

          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default EditTeamAssignmentsModal;
